import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plane, PartyPopper, Loader2, Sparkles, Trash2, CalendarRange } from 'lucide-react';
import { AuthContext, TasksContext } from '@/App';
import TripReviewCard from '@/components/TripReviewCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tz = () => -new Date().getTimezoneOffset();
const today = () => new Date().toISOString().slice(0, 10);
const inputCls = 'w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none';
const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', colorScheme: 'dark' };

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs text-gray-400 mb-1.5 block">{label}</span>{children}</label>
);

const TripCard = ({ trip, onCancel, headers }) => {
  const [plan, setPlan] = useState(trip.plan || '');
  const [busy, setBusy] = useState(false);
  const isEvent = trip.kind === 'event';
  const genPlan = async () => {
    setBusy(true);
    try { setPlan((await axios.post(`${API}/trips/${trip.id}/plan`, {}, { headers })).data.plan); }
    catch { toast.error('Could not create a plan'); }
    finally { setBusy(false); }
  };
  return (
    <div className="glass-card p-5" data-testid={`trip-card-${trip.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)' }}>
          {isEvent ? <PartyPopper size={16} className="text-amber-300"/> : <Plane size={16} className="text-amber-300"/>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">{trip.title || trip.destination || (isEvent ? 'Event' : 'Trip')}</p>
          <p className="text-xs text-gray-400">{trip.start_date}{trip.start_time ? ` ${trip.start_time}` : ''} → {trip.end_date}{trip.end_time ? ` ${trip.end_time}` : ''} · {isEvent ? 'protected event' : 'trip — normal scheduling paused'}</p>
        </div>
        <button onClick={genPlan} disabled={busy} data-testid={`trip-plan-btn-${trip.id}`}
          className="text-xs px-3 py-1.5 rounded-lg text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/10 flex items-center gap-1">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} {plan ? 'Re-plan' : 'Plan with AI'}
        </button>
        <button onClick={() => onCancel(trip)} data-testid={`trip-cancel-btn-${trip.id}`} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400"><Trash2 size={14}/></button>
      </div>
      {plan && <p className="mt-3 text-sm text-gray-300 whitespace-pre-line border-t border-white/5 pt-3" data-testid={`trip-plan-${trip.id}`}>{plan}</p>}
    </div>
  );
};

const TripsEvents = () => {
  const { auth } = useContext(AuthContext);
  const { bumpTasks } = useContext(TasksContext);
  const headers = { Authorization: `Bearer ${auth.token}` };
  const [kind, setKind] = useState('trip');
  const [form, setForm] = useState({ title: '', start_date: today(), end_date: today(), start_time: '', end_time: '' });
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [trips, setTrips] = useState([]);

  const load = () => axios.get(`${API}/trips`, { headers }).then(r => setTrips(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = () => ({
    kind, title: form.title || null, destination: kind === 'trip' ? (form.title || null) : null,
    start_date: form.start_date, end_date: form.end_date,
    start_time: kind === 'event' && form.start_time ? form.start_time : null,
    end_time: kind === 'event' && form.end_time ? form.end_time : null,
    tz_offset_minutes: tz(),
  });

  const check = async () => {
    if (!form.title.trim()) { toast.error(kind === 'trip' ? 'Where are you going?' : 'Name the event'); return; }
    setChecking(true);
    try { setPreview((await axios.post(`${API}/trips/preview`, payload(), { headers })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not check those dates'); }
    finally { setChecking(false); }
  };

  const apply = async (trip, decisions) => {
    try {
      const res = await axios.post(`${API}/trips/apply`, { ...payload(), decisions }, { headers });
      const r = res.data.results || [];
      toast.success(`${trip.label} saved — ${r.filter(x => x.action === 'rescheduled').length} moved, ${r.filter(x => x.action.startsWith('paused')).length} paused`);
      bumpTasks();
      setPreview(null);
      setForm(f => ({ ...f, title: '' }));
      load();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); return false; }
  };

  const cancel = async (trip) => {
    try {
      const res = await axios.delete(`${API}/trips/${trip.id}`, { headers });
      toast.success(`Removed — ${res.data.restored_tasks} paused task(s) restored`);
      bumpTasks();
      load();
    } catch { toast.error('Could not remove'); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen pt-20 px-4 pb-16" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#fbbf24,#f97316)' }}>
              <CalendarRange size={16} color="#0A0E27"/>
            </div>
            <h1 className="heading-font text-2xl font-bold text-white">Trips & Events</h1>
          </div>
          <p className="text-gray-400 text-sm">Tell SpaceCal about travel or a big day. I'll find every task and routine it affects, let you choose what to move or pause, protect the dates, and help you plan. You can also just tell Ask SpaceCal "I'm going to Goa Oct 20–24".</p>
        </div>

        <div className="glass-card p-6 mb-6 space-y-4" data-testid="trip-form">
          <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'rgba(255,255,255,0.04)' }}>
            {[['trip', 'Trip', Plane], ['event', 'Event', PartyPopper]].map(([k, l, Icon]) => (
              <button key={k} onClick={() => { setKind(k); setPreview(null); }} data-testid={`trip-kind-${k}`}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm transition-colors ${kind === k ? 'text-amber-200 bg-amber-400/15' : 'text-gray-500 hover:text-gray-300'}`}>
                <Icon size={14}/> {l}
              </button>
            ))}
          </div>
          <Field label={kind === 'trip' ? 'Destination' : 'Event name'}>
            <input value={form.title} onChange={set('title')} data-testid="trip-title-input" className={inputCls} style={inputStyle}
              placeholder={kind === 'trip' ? 'e.g. Goa' : "e.g. Sister's wedding"}/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><input type="date" value={form.start_date} min={today()} onChange={set('start_date')} data-testid="trip-start-date" className={inputCls} style={inputStyle}/></Field>
            <Field label="End date"><input type="date" value={form.end_date} min={form.start_date} onChange={set('end_date')} data-testid="trip-end-date" className={inputCls} style={inputStyle}/></Field>
          </div>
          {kind === 'event' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts at (optional)"><input type="time" value={form.start_time} onChange={set('start_time')} data-testid="event-start-time" className={inputCls} style={inputStyle}/></Field>
              <Field label="Ends at (optional)"><input type="time" value={form.end_time} onChange={set('end_time')} data-testid="event-end-time" className={inputCls} style={inputStyle}/></Field>
            </div>
          )}
          <button onClick={check} disabled={checking} data-testid="trip-check-btn"
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-[#0A0E27] flex items-center justify-center gap-2 transition-transform hover:scale-[1.01] disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}>
            {checking ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Check what's affected
          </button>
          {preview && (
            <div data-testid="trip-preview">
              <p className="text-xs text-gray-400 mb-1">{preview.items.length ? `${preview.items.length} item(s) fall inside these dates — choose what to do with each:` : 'Nothing is scheduled in that window. Confirm to protect it:'}</p>
              <TripReviewCard key={JSON.stringify(preview.trip)} action={{ type: 'trip_review', ...preview }} onApply={apply}/>
            </div>
          )}
        </div>

        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Upcoming ({trips.length})</h2>
        <div className="space-y-3" data-testid="trips-list">
          {trips.length === 0 && <p className="text-sm text-gray-500">No trips or events yet.</p>}
          {trips.map(t => <TripCard key={t.id} trip={t} onCancel={cancel} headers={headers}/>)}
        </div>
      </div>
    </div>
  );
};

export default TripsEvents;
